import { Check } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import { useTranslation } from 'react-i18next';

import { evaluatePasswordRules } from '@identity/domain/value-objects';

export type PasswordRequirementsChecklistProps = {
  /** The password exactly as typed. It is checked on every keystroke. */
  value: string;
};

/**
 * The password policy, rule by rule, as the user types.
 *
 * It evaluates with the same function the schema and the use case use, instead of writing its own
 * regular expressions, so the three can never disagree about what a valid password is.
 *
 * @param {PasswordRequirementsChecklistProps} props - Component properties
 */
export const PasswordRequirementsChecklist = ({ value }: PasswordRequirementsChecklistProps) => {
  const { t } = useTranslation('identity');

  return (
    <ul className="flex flex-col gap-2 font-sans" data-testid="password-requirements-checklist">
      {evaluatePasswordRules(value).map((rule) => (
        <li
          className={cn('flex items-center gap-2 text-sm text-muted-foreground', { 'text-success': rule.isMet })}
          data-state={rule.isMet ? 'met' : 'unmet'}
          data-testid={`password-rule-${rule.id}`}
          key={rule.id}
        >
          <Check aria-hidden className="size-4 shrink-0" />
          {t(`passwordRules.${rule.id}`)}
        </li>
      ))}
    </ul>
  );
};
